import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Box, Button, Alert, CircularProgress, useTheme } from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import { Sunset } from 'lucide-react';
import useDailyPlanStore, { PlanTask } from '@/store/Employee/DailyPlanStore';
import { useAuth } from '@store/authStore';
import EmptyState from '@/modules/okr/components/OKRDashboard/shared/EmptyState';
import RitualDrawer from './RitualDrawer';
import TaskDecisionCard, { DecisionOption } from './TaskDecisionCard';
import { ctaSx, formatPlanDateLong } from './ritualStyles';

type Choice = { status: '' | 'completed' | 'not_completed'; reason: string };

const iconSx = { mr: 0.5, fontSize: 16 };

const EOD_OPTIONS: DecisionOption[] = [
  {
    value: 'completed',
    label: 'Completed',
    tone: 'positive',
    icon: <CheckCircleOutlineIcon sx={iconSx} />
  },
  {
    value: 'not_completed',
    label: 'Not completed',
    tone: 'negative',
    icon: <CancelOutlinedIcon sx={iconSx} />
  }
];

/** How long the completion moment lingers before the panel closes. */
const SUCCESS_MS = 900;

export interface EodReportPanelProps {
  open: boolean;
  tasks: PlanTask[];
  /** 'eod' = closing out today. 'retro' = catching up on a day whose EOD was missed. */
  tone: 'eod' | 'retro';
  /** Omit for today; set to reconcile a prior day retroactively. */
  forDate?: string;
  /** 1-based position in a backlog of unreconciled days, if there is one. */
  dayIndex?: number;
  /** Size of that backlog. Shown only when > 1. */
  dayTotal?: number;
  /** Absent => blocking gate (no dismiss). */
  onClose?: () => void;
  onSubmitted?: () => void;
  /** Blocking mode: called after the completion moment, to release the gate. */
  onDone?: () => void;
  /** Blocking mode: the way out when submitting can't succeed. */
  onRetry?: () => void;
}

/**
 * End-of-Day report = the indirect punch-out (today, or retroactively for a day
 * whose EOD was missed).
 *
 * Every still-planned task must be marked; "not completed" demands a reason.
 * A day with no tasks is vacuously valid — submitting simply closes it out.
 */
const EodReportPanel: React.FC<EodReportPanelProps> = ({
  open,
  tasks,
  tone,
  forDate,
  dayIndex,
  dayTotal,
  onClose,
  onSubmitted,
  onDone,
  onRetry
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const { user } = useAuth();

  const submitEod = useDailyPlanStore((s) => s.submitEod);
  const submitting = useDailyPlanStore((s) => s.submitting);
  const error = useDailyPlanStore((s) => s.error);

  const [choices, setChoices] = useState<Record<number, Choice>>({});
  const [succeeded, setSucceeded] = useState(false);

  // Cleared on unmount: a backlog remounts this panel per day, and a timer left
  // running from the previous day would fire onDone into the new one and skip it.
  const successTimer = useRef<number | undefined>(undefined);
  useEffect(() => () => window.clearTimeout(successTimer.current), []);

  // Every task must always have a COMPLETE choice object — a partial entry would
  // make `.trim()` throw and blank the screen.
  useEffect(() => {
    setChoices((prev) => {
      let changed = false;
      const next = { ...prev };
      tasks.forEach((t) => {
        if (!next[t.id]) {
          next[t.id] = { status: '', reason: '' };
          changed = true;
        }
      });
      // `tasks` is often a freshly-filtered array from the parent, so this effect
      // can re-run on any parent render. Returning `prev` unchanged lets React bail
      // out instead of looping render -> setState -> render.
      return changed ? next : prev;
    });
  }, [tasks]);

  const setStatus = (taskId: number, status: Choice['status']) =>
    setChoices((prev) => ({
      ...prev,
      [taskId]: { status, reason: prev[taskId]?.reason || '' }
    }));

  const setReason = (taskId: number, reason: string) =>
    setChoices((prev) => ({
      ...prev,
      [taskId]: { status: prev[taskId]?.status || '', reason }
    }));

  const isValid = useMemo(
    () =>
      tasks.every((t) => {
        const c = choices[t.id];
        if (!c || !c.status) return false;
        if (c.status === 'not_completed' && !(c.reason || '').trim()) return false;
        return true;
      }),
    [tasks, choices]
  );

  const answered = useMemo(
    () => tasks.filter((t) => !!choices[t.id]?.status).length,
    [tasks, choices]
  );

  // A day with no tasks is already "done" — show a full bar rather than an empty one.
  const progress = tasks.length === 0 ? 100 : (answered / tasks.length) * 100;

  const handleSubmit = async () => {
    const taskStatuses = tasks.map((t) => {
      const c = choices[t.id] || { status: '', reason: '' };
      return {
        taskId: t.id,
        status: c.status as 'completed' | 'not_completed',
        incompleteReason:
          c.status === 'not_completed' ? (c.reason || '').trim() : undefined
      };
    });

    const ok = await submitEod({ forDate, taskStatuses });
    if (!ok) return;

    setSucceeded(true);
    successTimer.current = window.setTimeout(() => {
      onSubmitted?.();
      onClose?.();
      onDone?.();
    }, SUCCESS_MS);
  };

  const isRetro = tone === 'retro';
  const hasBacklog = isRetro && !!dayTotal && dayTotal > 1;
  const moreToGo = hasBacklog && (dayIndex ?? 1) < (dayTotal ?? 1);

  const title = isRetro
    ? `Let's close out ${formatPlanDateLong(forDate)}`
    : user?.firstName
      ? `How did today go, ${user.firstName}?`
      : 'How did today go?';

  const subtitle = isRetro
    ? hasBacklog
      ? // Say the backlog out loud. Silently reopening for the next day is what made
        // this feel like a loop the user couldn't escape.
        `A few days went unreported — we'll walk back through them, oldest first.`
      : "Looks like this one slipped — quick recap and we'll get you into today."
    : 'Wrap up before you head out.';

  const successCopy = isRetro
    ? {
        title: moreToGo ? 'Got it — on to the next day.' : 'Nice — now let’s plan today.',
        subtitle: undefined
      }
    : {
        title: user?.firstName ? `Nice work today, ${user.firstName}.` : 'Nice work today.',
        subtitle: 'See you tomorrow.'
      };

  const footerNote = !isValid
    ? 'Mark every task to finish up.'
    : `${answered} of ${tasks.length} marked`;

  return (
    <RitualDrawer
      open={open}
      tone={isRetro ? 'retro' : 'eod'}
      eyebrow={
        isRetro ? (hasBacklog ? `CATCHING UP · DAY ${dayIndex} OF ${dayTotal}` : 'CATCHING UP') : undefined
      }
      title={title}
      subtitle={subtitle}
      progress={progress}
      onClose={onClose}
      footerNote={tasks.length === 0 ? '' : footerNote}
      success={succeeded ? successCopy : null}
      recovery={
        error && onRetry ? (
          <Button onClick={onRetry} color="inherit" sx={{ textTransform: 'none' }}>
            Refresh
          </Button>
        ) : undefined
      }
      primaryAction={
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={!isValid || submitting}
          sx={ctaSx(theme, isRetro ? 'primary' : 'error')}
          startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : undefined}
        >
          {submitting
            ? isRetro
              ? 'Closing out…'
              : 'Closing out…'
            : isRetro
              ? 'Close Out & Plan Today'
              : 'Finish My Day'}
        </Button>
      }
    >
      {error && (
        <Alert severity="error" sx={{ mb: 2.5, borderRadius: 2 }}>
          {error}
        </Alert>
      )}

      {tasks.length === 0 ? (
        <EmptyState
          isDarkMode={isDark}
          compact
          icon={<Sunset size={24} color={isDark ? '#64748b' : '#94a3b8'} />}
          title={
            isRetro ? 'No tasks were recorded for that day' : 'Nothing was planned today'
          }
          description={
            isRetro
              ? "Submitting will simply close it out — then we'll plan today."
              : 'Submitting will simply close out your day.'
          }
        />
      ) : (
        <Box>
          {tasks.map((task, i) => {
            const c = choices[task.id];
            return (
              <TaskDecisionCard
                key={task.id}
                task={task}
                index={i}
                options={EOD_OPTIONS}
                value={c?.status || ''}
                onChange={(v) => setStatus(task.id, v as Choice['status'])}
                reason={c?.reason || ''}
                onReasonChange={(v) => setReason(task.id, v)}
                reasonRequiredFor="not_completed"
                reasonLabel="What got in the way?"
                reasonPlaceholder='e.g. "Blocked on review"'
                groupLabel={`Status for task: ${task.title}`}
              />
            );
          })}
        </Box>
      )}
    </RitualDrawer>
  );
};

export default EodReportPanel;
